import classNames from 'classnames';
import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
  InputHTMLAttributes,
  FocusEvent,
  ChangeEvent,
  MutableRefObject,
} from 'react';
import InputMask from 'react-input-mask';
import InputTextMask, { Mask } from 'react-text-mask';

import { FormInputWrapper } from '@/components/old';

import { FormConstants } from '../constants';
import { ValidateInputValueResult } from '../Validation';

interface BaseProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  value: string;
  label?: Nullable<string>;
  isAnt?: boolean;
  change: (name: string, value: string) => void;
  validator?: (value: string) => ValidateInputValueResult;
}

interface PropsWithMask extends BaseProps {
  withMask: true;
  withTextMask?: false;
  formatInputValue: (value: string) => string;
  mask: string | Array<string | RegExp>;
  inputMaskType: React.HTMLInputTypeAttribute;
}

interface PropsWithTextMask extends BaseProps {
  withTextMask: true;
  withMask?: false;
  formatInputValue: (value: string) => string;
  mask: Mask | ((value: string) => Mask);
  inputMaskType: React.HTMLInputTypeAttribute;
}

interface PropsWithoutMask extends BaseProps {
  withMask?: false;
  withTextMask?: false;
  formatInputValue?: never;
  mask?: never;
  inputMaskType?: never;
}

export type InputFieldProps = PropsWithMask | PropsWithoutMask | PropsWithTextMask;

export interface InputFieldHandle {
  triggerBlur: () => void;
}

export const InputField = forwardRef<InputFieldHandle, InputFieldProps>(
  (
    {
      name,
      value = '',
      placeholder = 'Не вказано',
      label = '',
      required = false,
      disabled = false,
      withMask = false,
      withTextMask = false,
      isAnt = false,
      mask = '',
      inputMaskType = '',
      change,
      validator,
      formatInputValue,
      ...inputHTMLAttributes
    },
    ref,
  ) => {
    const [inputError, setInputError] = useState<string>('');
    const inputRef = useRef<HTMLInputElement | null>(null) as MutableRefObject<HTMLInputElement | null>;

    useEffect(() => {
      if (value) validateField(value);
    }, []);

    const validateField = (inputValue = ''): void => {
      if (required && !inputValue) {
        setInputError('Необхідно заповнити поле');
        return;
      }

      if (inputHTMLAttributes.minLength && inputValue && inputValue.length < inputHTMLAttributes.minLength) {
        setInputError(`Повинно бути не менше ${inputHTMLAttributes.minLength} символів`);
        return;
      }

      if (inputHTMLAttributes.maxLength && inputValue && inputValue.length > inputHTMLAttributes.maxLength) {
        setInputError(`Повинно бути не більше ${inputHTMLAttributes.maxLength} символів`);
        return;
      }

      if (inputValue && validator) {
        const { isValid, error } = validator(inputValue);
        setInputError(!isValid && error ? error : '');
      }
    };

    const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
      const newValue = formatInputValue ? formatInputValue(event.target.value) : event.target.value;
      change(event.target.name, newValue);
      setInputError('');
    };

    const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
      const trimmedValue = formatInputValue ? formatInputValue(event.target.value.trim()) : event.target.value.trim();
      change(event.target.name, trimmedValue);
      validateField(trimmedValue);
    };

    useImperativeHandle(ref, () => ({
      triggerBlur: (): void => {
        handleBlur({
          target: {
            name,
            value,
          },
        } as FocusEvent<HTMLInputElement>);
      },
    }));

    const handleTextMaskRef = (masked: any) => {
      inputRef.current = masked?.inputElement ?? null;
    };

    const commonProps = {
      ...inputHTMLAttributes,
      name,
      value,
      placeholder,
      disabled,
      onChange,
      onBlur: handleBlur,
      className: withMask || withTextMask ? 'form-input__input form-control' : 'form-input__input',
    };

    return (
      <FormInputWrapper
        label={label || undefined}
        required={required}
        marginBottomNone
        disabled={disabled}
        className={classNames({
          'disabled-div': disabled,
          'error-div': !!inputError,
          'form-input--ant': isAnt,
        })}
      >
        {withMask && (
          <InputMask {...commonProps} type={inputMaskType} mask={mask as PropsWithMask['mask']} inputRef={inputRef} />
        )}

        {withTextMask && (
          <InputTextMask
            {...commonProps}
            type={inputMaskType}
            mask={mask as PropsWithTextMask['mask']}
            ref={handleTextMaskRef}
          />
        )}

        {!withMask && !withTextMask && (
          <input
            {...commonProps}
            type="text"
            maxLength={inputHTMLAttributes.maxLength ?? FormConstants.INPUT_MAX_LENGTH}
            ref={inputRef}
          />
        )}

        {inputError && <div className="error-msg">{inputError}</div>}
      </FormInputWrapper>
    );
  },
);
